import * as React from 'react';
import { FilterInterface } from '../../../Filter/FilterInterface';
import { RangeFilterValueType } from '../../../Filter/RangeFilter';
import { RangeFilterValue } from '../../../Filter/RangeFilterValue';
import { AbstractFilterView, OptionsFilterViewProps, OptionsType } from '../AbstractFilterView';
import { FilterEvent } from '../FilterEvent';
import { OptionsCollection } from '../OptionsCollection';

export interface RangeFilterProps<VALUE_TYPE, OPTION_TYPE> extends OptionsFilterViewProps<VALUE_TYPE, OPTION_TYPE> {
    /**
     * Define if the components should trigger a FilterEvent if it has been initialized with the boundaries detected
     * from the options
     */
    triggerOnInit?: boolean

    showErrors?: boolean
}

export abstract class AbstractRangeFilterView<VALUE_TYPE extends RangeFilterValueType,
    OPTION_TYPE,
    P extends RangeFilterProps<VALUE_TYPE, OPTION_TYPE>,
    >
    extends AbstractFilterView<VALUE_TYPE, P> {
    private _didInitialize: boolean;

    constructor(props: Readonly<P>) {
        super(props);
        this._didInitialize = false;
        // this.handleUpdate = this.handleUpdate.bind(this);
    }

    protected abstract renderInputInner(
        props: P,
        filter: FilterInterface<VALUE_TYPE>,
        options: OptionsType<OPTION_TYPE>,
        bounds: RangeFilterValue,
        currentValue: RangeFilterValue
    ): JSX.Element | null;

    protected renderInput(): JSX.Element | null {
        const props = this.props;
        const options = props.options;
        if (this.isEmpty(options)) {
            return props.showErrors ? <div className="pf-error">No options available</div> : null;
        }
        const filter = props.filter;
        const value = filter.value;
        const bounds = this.getOptionsBounds(options);
        const availableMin = bounds.min;
        const availableMax = bounds.max;
        const hasMin = typeof value.min !== 'undefined';
        const hasMax = typeof value.max !== 'undefined';

        if ((!hasMin || !hasMax) && props.triggerOnInit) {
            setTimeout(
                () => {
                    console.info(`[${this}] Trigger on init`);
                    const filterEvent = new FilterEvent(undefined, bounds);
                    this.setDidInitialize();
                    this.handleChange<RangeFilterValue>(filterEvent);
                },
                100
            );
        }

        const currentValue = new RangeFilterValue(
            hasMin ? value.min : availableMin,
            hasMax ? value.max : availableMax
        );

        return this.renderInputInner(props, filter, options, bounds, currentValue);
    }

    get didInitialize(): boolean {
        return this._didInitialize;
    }

    protected setDidInitialize() {
        this._didInitialize = true;
    }

    protected isEmpty(options: OptionsType<OPTION_TYPE>): boolean {
        if (!options) {
            return true;
        }
        if (options instanceof OptionsCollection && options.length === 0) {
            return true;
        }
        if (Array.isArray(options) && options.length === 0) {
            return true;
        }
        return Object.keys(options).length === 0;
    }

    protected getOptionsBounds(options: OptionsType<OPTION_TYPE>): RangeFilterValue {
        const col = this.getOptionsAsNumbers(options);

        return new RangeFilterValue(
            Math.min(...col),
            Math.max(...col),
        );
    }

    protected getOptionsAsNumbers(options: OptionsType<OPTION_TYPE>): number[] {
        const colStrings = OptionsCollection.from(options).keys();
        if (colStrings.length === 0) {
            throw new TypeError('Options must not be empty');
        }
        return colStrings.map(o => parseInt(o, 10)).filter(o => !isNaN(o));
    }
}
